import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import PublicChrome from './public/PublicChrome';
import BlurFade from './public/motion/BlurFade';
import { getImageProxyUrl } from '../utils/imageUtils';
import SeoHead, { breadcrumbJsonLd } from './seo/SeoHead';

const convertProxyToDirectUrl = (url: string): string => {
  if (url.includes('/api/image-proxy/blog/')) {
    const filename = url.split('/').pop();
    return `/uploads/images/blogs/${filename}`;
  }
  return url;
};

interface Blog {
  _id: string;
  title: string;
  description: string;
  content: string;
  author: string;
  category: string;
  tags: string[];
  slug: string;
  isPublished: boolean;
  featured: boolean;
  readTime: number;
  seoTitle?: string;
  seoDescription?: string;
  views: number;
  likes: number;
  imageUrl?: string;
  createdAt: string;
  updatedAt: string;
}

const BlogListPage: React.FC = () => {
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [categories, setCategories] = useState<string[]>([]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return `${text.substring(0, maxLength).trim()}…`;
  };

  const fetchBlogs = async (page: number = 1, category: string = 'all', search: string = '') => {
    try {
      setLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;

      let url = `${apiUrl}/api/blogs?page=${page}&limit=12&sort=-createdAt`;
      if (category !== 'all') url += `&category=${category}`;
      if (search.trim()) url += `&search=${encodeURIComponent(search.trim())}`;

      const response = await axios.get(url);

      if (response.data.blogs) {
        setBlogs(response.data.blogs);
        setTotalPages(response.data.totalPages || 1);
        setCurrentPage(response.data.currentPage || 1);
      }
    } catch (error) {
      console.error('Error fetching blogs:', error);
      setBlogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
        const response = await axios.get(`${apiUrl}/api/blogs/categories`);
        setCategories(Array.isArray(response.data.categories) ? response.data.categories : []);
      } catch (error) {
        console.error('Error fetching blog categories:', error);
        setCategories([]);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchBlogs(currentPage, selectedCategory, searchQuery);
  }, [currentPage, selectedCategory]);

  useEffect(() => {
    if (
      selectedCategory !== 'all' &&
      categories.length > 0 &&
      !categories.includes(selectedCategory)
    ) {
      setSelectedCategory('all');
    }
  }, [categories, selectedCategory]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    clearTimeout((window as any).searchTimeout);
    (window as any).searchTimeout = setTimeout(() => {
      setCurrentPage(1);
      fetchBlogs(1, selectedCategory, value);
    }, 500);
  };

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const filterCls = (active: boolean) =>
    `px-0 py-2 text-[0.72rem] font-semibold tracking-[0.12em] uppercase border-b-2 transition-colors bg-transparent cursor-pointer ${
      active
        ? 'text-[var(--dusk-ink)] border-[var(--dusk-copper)]'
        : 'text-[var(--dusk-muted)] border-transparent hover:text-[var(--dusk-ink)]'
    }`;

  return (
    <PublicChrome>
      <SeoHead
        title="Blog"
        description="Insights and notes on validators, staking, and blockchain infrastructure from DUSKPROOF."
        path="/blog"
        jsonLd={breadcrumbJsonLd([
          { name: 'Home', path: '/' },
          { name: 'Blog', path: '/blog' },
        ])}
      />
      <section className="dusk-section !pt-16 md:!pt-24 !pb-10">
        <div className="dusk-container">
          <BlurFade>
            <h1 className="dusk-page-title text-[clamp(2.5rem,6vw,4rem)] m-0 mb-5 leading-tight">
              Blog
            </h1>
            <p className="text-[var(--dusk-muted)] text-base md:text-lg leading-relaxed max-w-xl m-0">
              Insights and notes on validators, staking, and blockchain infrastructure from DUSKPROOF.
            </p>
          </BlurFade>
        </div>
      </section>

      <section className="pb-8">
        <div className="dusk-container">
          <div className="flex flex-col gap-8 md:flex-row md:items-end md:justify-between border-b border-[var(--dusk-line)] pb-6">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setCurrentPage(1);
                fetchBlogs(1, selectedCategory, searchQuery);
              }}
              className="w-full max-w-sm"
            >
              <label className="block text-[0.65rem] font-semibold tracking-[0.16em] uppercase text-[var(--dusk-faint)] mb-2">
                Search
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder="Search articles…"
                  className="w-full bg-transparent border-0 border-b border-[var(--dusk-line-strong)] rounded-none py-2.5 pr-8 text-[var(--dusk-ink)] placeholder-[var(--dusk-faint)] focus:outline-none focus:border-[var(--dusk-copper)]"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchQuery('');
                      setCurrentPage(1);
                      fetchBlogs(1, selectedCategory, '');
                    }}
                    className="absolute right-0 top-1/2 -translate-y-1/2 text-[var(--dusk-muted)] hover:text-[var(--dusk-ink)] bg-transparent border-0 cursor-pointer text-lg leading-none"
                    aria-label="Clear search"
                  >
                    ×
                  </button>
                )}
              </div>
            </form>

            <div className="flex flex-wrap gap-5 md:gap-6">
              <button type="button" onClick={() => handleCategoryChange('all')} className={filterCls(selectedCategory === 'all')}>
                All
              </button>
              {categories.map((category) => (
                <button
                  key={category}
                  type="button"
                  onClick={() => handleCategoryChange(category)}
                  className={filterCls(selectedCategory === category)}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="dusk-section !pt-10 !pb-24">
        <div className="dusk-container">
          {loading && blogs.length === 0 ? (
            <p className="text-[var(--dusk-muted)] py-16 text-center">Loading…</p>
          ) : blogs.length === 0 ? (
            <div className="py-20 text-center">
              <p className="font-display text-2xl text-[var(--dusk-ink)] m-0 mb-3">No articles found</p>
              <p className="text-[var(--dusk-muted)] m-0">
                {searchQuery ? 'Try another search or clear filters.' : 'New posts will appear here soon.'}
              </p>
            </div>
          ) : (
            <>
              <div className="border-t border-[var(--dusk-line)]">
                {blogs.map((blog, i) => (
                  <BlurFade key={blog._id} delay={0.03 * Math.min(i, 8)}>
                    <Link
                      to={`/blog/${blog.slug}`}
                      className="group grid md:grid-cols-[minmax(0,1.4fr)_minmax(0,12rem)] gap-6 md:gap-10 py-10 px-4 md:px-6 border-b border-[var(--dusk-line)] no-underline items-start hover:bg-[rgba(208,0,104,0.04)] transition-colors"
                    >
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-3 text-[0.7rem] tracking-[0.1em] uppercase text-[var(--dusk-faint)]">
                          <span className="text-[var(--dusk-copper)]">{blog.category}</span>
                          <span>{formatDate(blog.createdAt)}</span>
                          {blog.readTime ? <span>{blog.readTime} min</span> : null}
                          {blog.author ? <span>{blog.author}</span> : null}
                        </div>
                        <h2 className="font-display text-2xl md:text-[1.75rem] text-[var(--dusk-ink)] m-0 mb-3 leading-snug group-hover:text-[var(--dusk-amber)] transition-colors">
                          {blog.title}
                        </h2>
                        {blog.description && (
                          <p className="text-[var(--dusk-muted)] text-[0.95rem] leading-[1.7] m-0 max-w-2xl">
                            {truncateText(blog.description, 160)}
                          </p>
                        )}
                        <span className="inline-block mt-5 text-[0.72rem] font-semibold tracking-[0.14em] uppercase text-[var(--dusk-copper)]">
                          READ MORE →
                        </span>
                      </div>
                      {blog.imageUrl ? (
                        <span
                          className="dusk-media-frame order-first md:order-none w-full max-w-[12rem] md:justify-self-end"
                          style={
                            {
                              ['--dusk-border-delay' as string]: `${-((i * 1.15) % 2.6)}s`,
                            } as React.CSSProperties
                          }
                        >
                          <span className="dusk-media-shot aspect-[16/10]">
                            <img
                              src={getImageProxyUrl(convertProxyToDirectUrl(blog.imageUrl))}
                              alt=""
                              className="dusk-media-shot__img !h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          </span>
                        </span>
                      ) : (
                        <div className="hidden md:block" />
                      )}
                    </Link>
                  </BlurFade>
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex flex-wrap justify-center items-center gap-3 mt-14">
                  <button
                    type="button"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="dusk-btn-ghost !py-2 !px-4 disabled:opacity-40"
                  >
                    ← Prev
                  </button>
                  <span className="text-sm text-[var(--dusk-muted)] tabular-nums px-2">
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="dusk-btn-ghost !py-2 !px-4 disabled:opacity-40"
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </PublicChrome>
  );
};

export default BlogListPage;
